<template>
  <div class="em-select" ref="selectRef" v-bind="attrs">
    <div
      class="select-trigger"
      :class="{
        active: isOpen,
        disabled: props.disabled,
        'has-value': hasValue,
        'has-label': !!props.label,
        'has-floating-label': props.floatingLabel,
        rounded: props.rounded,
      }"
      :style="props.height ? { height: props.height } : undefined"
      @click="toggleDropdown"
    >
      <label v-if="props.label" :class="labelClasses" class="floating-label">
        {{ props.label }}
      </label>
      <label v-else-if="props.floatingLabel" :class="labelClasses" class="floating-label">
        {{ props.placeholder }}
      </label>
      <span class="select-value" :class="{ placeholder: !hasValue }">
        {{ displayText }}
      </span>
      <span v-if="loading" class="button-spinner">
        <IonSpinner name="crescent" />
      </span>
      <span v-else class="select-arrow" :class="{ rotate: isOpen }">
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    </div>
  </div>

  <!-- Dropdown teleported to body to escape overflow containers -->
  <Teleport to="body">
    <Transition name="dropdown">
      <div
        v-show="isOpen && !props.disabled"
        ref="dropdownRef"
        class="select-dropdown-teleported"
        :style="dropdownStyle"
      >
        <input
          v-if="props.searchable"
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="select-search"
          :placeholder="t('common.search')"
          @click.stop
          @input="handleSearchInput"
        />
        <div v-if="isLoading" class="select-loading">
          <span class="loading-spinner"></span>
          {{ t('common.loading') }}
        </div>
        <ul v-else class="select-options">
          <!-- Multiple selection with checkboxes -->
          <template v-if="props.multiple">
            <li
              v-for="option in filteredOptions"
              :key="getOptionKey(option)"
              class="select-option"
              :class="{ selected: isSelected(option.value) }"
              @click.stop="handleMultipleSelect(option.value)"
            >
              <input
                type="checkbox"
                :checked="isSelected(option.value)"
                @click.stop
                @change="handleMultipleSelect(option.value)"
              />
              <span v-if="option.icon" class="option-icon">{{ option.icon }}</span>
              {{ option.label }}
            </li>
          </template>
          <!-- Single selection -->
          <template v-else>
            <li
              v-for="option in filteredOptions"
              :key="getOptionKey(option)"
              class="select-option"
              :class="{ selected: isSelected(option.value) }"
              @click="handleSingleSelect(option.value)"
            >
              <span v-if="option.icon" class="option-icon">{{ option.icon }}</span>
              {{ option.label }}
            </li>
          </template>
          <li v-if="filteredOptions.length === 0" class="no-results">
            {{ t('common.noResults') }}
          </li>
        </ul>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, useAttrs } from 'vue';
import { useLocale } from '@/common/composables/useLocale';
import { logger } from '@/common/services/config';
import { IonSpinner } from '@ionic/vue';

// Disable automatic attribute inheritance since we use Teleport
defineOptions({
  inheritAttrs: false,
});

// Get non-prop attributes to apply manually
const attrs = useAttrs();

// Strongly typed option interface
export interface SelectOption {
  value: string | number | null | Record<string, any>;
  label: string;
  icon?: string;
  [key: string]: any;
}

type OptionValue = string | number | null | SelectOption | Record<string, any>;
type SingleValue = OptionValue | null;
type MultipleValue = OptionValue[];
type ModelValue = SingleValue | MultipleValue;

// Search function type for async search
export type SearchFunction = (query: string) => Promise<SelectOption[]>;

// Typed props with defaults
const props = withDefaults(
  defineProps<{
    modelValue: ModelValue;
    options?: SelectOption[];
    label?: string;
    placeholder?: string;
    floatingLabel?: boolean;
    rounded?: boolean;
    searchable?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    dropdownPosition?: 'top' | 'bottom';
    searchFunction?: SearchFunction;
    minSearchLength?: number;
    debounceMs?: number;
    /** Custom height for the component (e.g. '200px', '100%') */
    height?: string;
    loading?: boolean;
  }>(),
  {
    options: () => [],
    label: '',
    placeholder: 'Sélectionner...',
    floatingLabel: false,
    searchable: false,
    disabled: false,
    multiple: false,
    dropdownPosition: 'bottom',
    minSearchLength: 2,
    debounceMs: 300,
  }
);

// Typed emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: ModelValue): void;
}>();

// Composables
const { t } = useLocale();

// Refs
const selectRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const searchInput = ref<HTMLInputElement | null>(null);
const isOpen = ref<boolean>(false);
const searchQuery = ref<string>('');
const isLoading = ref<boolean>(false);
const asyncOptions = ref<SelectOption[]>([]);
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
const dropdownCoords = ref({ top: 0, left: 0, width: 0 });

// Computed style for teleported dropdown
const dropdownStyle = computed(() => ({
  position: 'fixed' as const,
  top: `${dropdownCoords.value.top}px`,
  left: `${dropdownCoords.value.left}px`,
  width: `${dropdownCoords.value.width}px`,
  zIndex: 99999,
}));

// Update dropdown position based on trigger element
const updateDropdownPosition = () => {
  if (!selectRef.value) return;
  const rect = selectRef.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Use actual dropdown height if already rendered, otherwise estimate max
  const dropdownHeight = dropdownRef.value?.offsetHeight || 208;

  // Determine if dropdown should open upward
  const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

  dropdownCoords.value = {
    top: openUpward ? rect.top - dropdownHeight : rect.bottom + 4,
    left: rect.left,
    width: rect.width,
  };
};

// Check if there's a value
const hasValue = computed(() => {
  if (props.multiple) {
    return Array.isArray(props.modelValue) && props.modelValue.length > 0;
  }
  return props.modelValue !== null && props.modelValue !== '' && props.modelValue !== undefined;
});

// Label classes for floating effect
const labelClasses = computed(() => ({
  'floating-label--active': isOpen.value || hasValue.value,
}));

// Display text
const displayText = computed<string>(() => {
  if (!hasValue.value) {
    return props.label || props.floatingLabel ? '' : props.placeholder;
  }

  if (props.multiple) {
    const values = props.modelValue as MultipleValue;
    const labels = values
      .map((val) => props.options.find((opt) => opt.value === val)?.label)
      .filter(Boolean);
    return labels.join(', ') || props.placeholder;
  }

  const selected = props.options.find((opt) => opt.value === props.modelValue);
  if (selected) {
    return selected.icon ? `${selected.icon} ${selected.label}` : selected.label;
  }
  return props.placeholder;
});

// Filtered options
const filteredOptions = computed<SelectOption[]>(() => {
  // If using async search function, return async results
  if (props.searchFunction) {
    return asyncOptions.value;
  }
  // Otherwise, filter local options
  if (!searchQuery.value) return props.options;
  const query = searchQuery.value.toLowerCase();
  return props.options.filter((option) => option.label.toLowerCase().includes(query));
});

// Handle async search input with debounce
const handleSearchInput = async () => {
  if (!props.searchFunction) return;

  // Clear previous timer
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }

  const query = searchQuery.value.trim();

  // Check minimum length
  if (query.length < props.minSearchLength) {
    asyncOptions.value = [];
    return;
  }

  // Debounce the search
  searchDebounceTimer = setTimeout(async () => {
    isLoading.value = true;
    try {
      const results = await props.searchFunction!(query);
      asyncOptions.value = results;
    } catch (error) {
      logger.error('Error during async search:', error);
      asyncOptions.value = [];
    } finally {
      isLoading.value = false;
    }
  }, props.debounceMs);
};

// Check if value is selected
const isSelected = (value: OptionValue): boolean => {
  if (props.multiple) {
    const values = (props.modelValue as MultipleValue) || [];
    return values.some((v) => {
      if (typeof v === 'object' && typeof value === 'object') {
        return JSON.stringify(v) === JSON.stringify(value);
      }
      return v === value;
    });
  }
  if (typeof props.modelValue === 'object' && typeof value === 'object') {
    return JSON.stringify(props.modelValue) === JSON.stringify(value);
  }
  return props.modelValue === value;
};

// Get unique key for option
const getOptionKey = (option: SelectOption): string => {
  if (typeof option.value === 'object') {
    return JSON.stringify(option.value);
  }
  return String(option.value);
};

const toggleDropdown = (): void => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    updateDropdownPosition();
    nextTick(() => {
      // Recalculate with actual rendered dropdown height
      updateDropdownPosition();
      if (props.searchable) {
        searchInput.value?.focus();
      }
    });
  }
};

const handleSingleSelect = (value: OptionValue): void => {
  if (props.disabled) return;
  emit('update:modelValue', value);
  isOpen.value = false;
  searchQuery.value = '';
};

const handleMultipleSelect = (value: OptionValue): void => {
  if (props.disabled) return;
  const currentValues = ((props.modelValue as MultipleValue) || []).slice();

  const index = currentValues.findIndex((v) => {
    if (typeof v === 'object' && typeof value === 'object') {
      return JSON.stringify(v) === JSON.stringify(value);
    }
    return v === value;
  });

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(value);
  }

  emit('update:modelValue', currentValues);
};

const closeDropdown = (event: MouseEvent): void => {
  const target = event.target as Node | null;
  if (!target) return;

  // Check if click is inside the trigger or the teleported dropdown
  const isInsideTrigger = selectRef.value?.contains(target);
  const isInsideDropdown = dropdownRef.value?.contains(target);

  if (!isInsideTrigger && !isInsideDropdown) {
    isOpen.value = false;
  }
};

const handleEscape = (event: KeyboardEvent): void => {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false;
  }
};

watch(isOpen, (newValue: boolean) => {
  if (!newValue) {
    searchQuery.value = '';
    asyncOptions.value = [];
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
  }
});

// Handle scroll and resize to reposition dropdown
const handleScrollOrResize = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
  document.addEventListener('keydown', handleEscape);
  window.addEventListener('scroll', handleScrollOrResize, true);
  window.addEventListener('resize', handleScrollOrResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeDropdown);
  document.removeEventListener('keydown', handleEscape);
  window.removeEventListener('scroll', handleScrollOrResize, true);
  window.removeEventListener('resize', handleScrollOrResize);
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});
</script>

<style scoped>
.em-select {
  position: relative;
  width: 100%;
}

.select-trigger {
  width: 100%;
  padding: 12px 16px;
  background: var(--ion-background-color);
  border: 1px solid var(--ion-input-border-color);
  border-radius: var(--ion-radius-lg);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  color: var(--ion-text-color);
  position: relative;
  min-height: 48px;
  min-width: 0;
  overflow: visible;
}
.select-trigger.rounded {
  border-radius: var(--ion-radius-lg);
}

.select-trigger.has-floating-label,
.select-trigger.has-label {
  padding-top: 18px;
  padding-bottom: 6px;
}

.select-trigger:hover:not(.disabled) {
  border-color: var(--ion-input-border-color-hover);
}

.select-trigger.active {
  border-color: var(--ion-color-primary);
}

.select-trigger.disabled {
  color: var(--ion-text-color-disabled);
  background-color: var(--ion-input-background-color-disabled);
  border-color: var(--ion-input-border-color-disabled);
  cursor: not-allowed;
}

.select-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-value.placeholder {
  color: var(--ion-input-placeholder-color);
}

.button-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.select-arrow {
  transition: transform 0.3s ease;
  font-size: 12px;
  color: var(--ion-text-color);
  display: flex;
  align-items: center;
}

.select-arrow.rotate {
  transform: rotate(180deg);
}

/* Floating label styles */
.floating-label {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: var(--ion-input-placeholder-color);
  pointer-events: none;
  transition: all 0.2s ease-in-out;
  background-color: var(--ion-background-color, #ffffff);
  padding: 0 4px;
  z-index: 1;
  border-radius: 3vmin;
}

.floating-label--active {
  top: 0;
  transform: translateY(-50%);
  font-size: 12px;
  color: var(--ion-text-color);
  z-index: 0;
  background-color: var(--ion-background-color);
  pointer-events: auto;
}

/* Dropdown - Teleported to body */
.select-dropdown-teleported {
  background: var(--ion-input-background, #ffffff);
  border: 1px solid var(--ion-color-primary);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
}

/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Search input */
.select-search {
  padding: 10px 16px;
  border: none;
  border-bottom: 1px solid var(--ion-input-border-color);
  width: 100%;
  font-size: 14px;
  outline: none;
  position: sticky;
  top: 0;
  background: var(--ion-input-background);
  z-index: 1;
}

.select-search:focus {
  border-bottom-color: var(--ion-color-primary);
}

/* Options */
.select-options {
  list-style: none;
  padding: 0;
  margin: 0;
}

.select-option {
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--ion-text-color);
}

.select-option:hover {
  background: var(--ion-input-background-color-selected);
}

.select-option.selected {
  background: var(--ion-input-background-color-selected);
  color: var(--ion-color-primary);
}

.select-option input[type='checkbox'] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--ion-color-primary);
}

.option-icon {
  font-size: 18px;
}

.no-results {
  padding: 16px;
  text-align: center;
  color: var(--ion-color-medium);
  font-size: 14px;
}

/* Loading state */
.select-loading {
  padding: 16px;
  text-align: center;
  color: var(--ion-color-medium);
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid var(--ion-color-light);
  border-top-color: var(--ion-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
